import type { DraftState, Manager, TeamLiveData } from '../types';
import { TEAMS_BY_ABBR } from '../constants/teams';
import { maxBidPower, teamsRemainingFor } from '../lib/draftLogic';
import { TeamLogo } from './TeamLogo';

interface Props {
  state: DraftState;
  manager: Manager;
  isLocalUser: boolean;
  isCommissioner: boolean;
  isActiveBidder: boolean;
  liveData: Record<string, TeamLiveData>;
  rankings: Record<string, number>;
}

export function ManagerCard({ state, manager, isLocalUser, isCommissioner, isActiveBidder, liveData, rankings }: Props) {
  const isNominee = state.managers[state.currentNomineeIndex]?.id === manager.id;
  const isComplete = manager.teamsDrafted >= state.rosterCap;
  const power = maxBidPower(state, manager.id);
  const remaining = teamsRemainingFor(state, manager.id);
  const spent = manager.initialBudget - manager.budget;

  // Compute season record sum of their teams
  const totalWins = manager.teams.reduce((sum, abbr) => sum + (liveData[abbr]?.record?.wins ?? 0), 0);
  const totalLosses = manager.teams.reduce((sum, abbr) => sum + (liveData[abbr]?.record?.losses ?? 0), 0);
  const avgRank = manager.teams.length
    ? Math.round(manager.teams.reduce((s, a) => s + (rankings[a] ?? 32), 0) / manager.teams.length)
    : null;

  // Spending pace: compare my $/team-so-far vs the league $/team-so-far.
  // Positive = overpaying relative to peers; negative = underspending.
  const myAvgCost = manager.teamsDrafted > 0 ? spent / manager.teamsDrafted : 0;
  const leagueDrafted = state.managers.reduce((s, m) => s + m.teamsDrafted, 0);
  const leagueSpent = state.managers.reduce((s, m) => s + (m.initialBudget - m.budget), 0);
  const leagueAvgCost = leagueDrafted > 0 ? leagueSpent / leagueDrafted : 0;
  const pacePct =
    leagueAvgCost > 0 && manager.teamsDrafted > 0
      ? ((myAvgCost - leagueAvgCost) / leagueAvgCost) * 100
      : null;
  const paceColor =
    pacePct == null
      ? 'text-slate-500'
      : pacePct >= 20
      ? 'text-rose-400'
      : pacePct <= -20
      ? 'text-emerald-400'
      : 'text-slate-300';
  const paceLabel =
    pacePct == null
      ? 'Pace n/a'
      : pacePct >= 20
      ? `Spending hot (+${Math.round(pacePct)}%)`
      : pacePct <= -20
      ? `Saving big (${Math.round(pacePct)}%)`
      : `On pace (${pacePct >= 0 ? '+' : ''}${Math.round(pacePct)}%)`;

  // Divisional coverage chips
  type DivKey = `${'AFC' | 'NFC'} ${'East' | 'North' | 'South' | 'West'}`;
  const divCounts: Record<string, number> = {};
  manager.teams.forEach(abbr => {
    const meta = TEAMS_BY_ABBR[abbr];
    if (!meta) return;
    const key: DivKey = `${meta.conference} ${meta.division}`;
    divCounts[key] = (divCounts[key] ?? 0) + 1;
  });
  const divChips = Object.entries(divCounts).sort((a, b) => b[1] - a[1]);

  const ring = isActiveBidder
    ? 'ring-4 ring-amber-400 shimmer'
    : isLocalUser
    ? 'ring-2 ring-teal-400'
    : '';

  return (
    <div className={`panel p-4 ${ring} ${isComplete ? 'opacity-70' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{manager.emoji}</span>
          <div>
            <div className="text-lg font-extrabold text-white">{manager.name}</div>
            <div className="flex gap-1 mt-0.5">
              {isCommissioner && <span className="pill bg-rose-600 text-white text-[10px]">COMMISH</span>}
              {isLocalUser && <span className="pill bg-teal-500 text-slate-900 text-[10px]">YOU</span>}
              {isNominee && state.phase === 'Nomination' && (
                <span className="pill bg-amber-400 text-slate-900 text-[10px]">NOMINATING</span>
              )}
              {isActiveBidder && state.phase === 'Bidding' && (
                <span className="pill bg-amber-400 text-slate-900 text-[10px]">ON THE CLOCK</span>
              )}
              {isComplete && <span className="pill bg-indigo-600 text-white text-[10px]">COMPLETE</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-extrabold text-emerald-400">${manager.budget}</div>
          <div className="text-[10px] text-slate-500">init ${manager.initialBudget}</div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2 text-center mb-3 text-xs">
        <Stat label="Teams" value={`${manager.teamsDrafted}/${state.rosterCap}`} />
        <Stat label="Max Bid" value={`$${power}`} />
        <Stat label="Spent" value={`$${spent}`} />
        <Stat label="Avg Rk" value={avgRank ? `#${avgRank}` : '—'} />
      </div>

      {/* Spending pace gauge */}
      {manager.teamsDrafted > 0 && (
        <div className="mb-3">
          <div className="flex justify-between text-[10px] uppercase mb-1">
            <span className="text-slate-500">Pace</span>
            <span className={`font-bold ${paceColor}`}>{paceLabel}</span>
          </div>
          <div className="relative h-1.5 bg-slate-800 rounded-full overflow-hidden">
            {/* Center marker = league average */}
            <div className="absolute left-1/2 top-0 h-full w-px bg-slate-500/70" />
            {pacePct != null && (() => {
              const clamped = Math.max(-100, Math.min(100, pacePct));
              const half = Math.abs(clamped) / 2; // map [-100..100] to [0..50]%
              const fillColor =
                clamped >= 20 ? 'bg-rose-500' : clamped <= -20 ? 'bg-emerald-500' : 'bg-slate-400';
              const style: React.CSSProperties = clamped >= 0
                ? { left: '50%', width: `${half}%` }
                : { right: '50%', width: `${half}%` };
              return (
                <div className={`absolute top-0 h-full ${fillColor}`} style={style} />
              );
            })()}
          </div>
          <div className="text-[9px] text-slate-500 mt-0.5">
            ${myAvgCost.toFixed(1)}/team · league ${leagueAvgCost.toFixed(1)}/team
          </div>
        </div>
      )}

      {/* Division coverage chips */}
      {divChips.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {divChips.map(([div, count]) => (
            <span
              key={div}
              className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300"
              title={`${count} team${count === 1 ? '' : 's'} from ${div}`}
            >
              {div} ×{count}
            </span>
          ))}
        </div>
      )}

      {/* Drafted team logos with prices */}
      <div className="border-t border-slate-700/60 pt-2">
        <div className="text-[10px] uppercase text-slate-400 mb-1.5">
          Roster · Season Record: <span className="text-white font-bold">{totalWins}-{totalLosses}</span>
        </div>
        {manager.teams.length === 0 ? (
          <p className="text-xs text-slate-500 italic">No teams yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {manager.teams.map(abbr => {
              const meta = TEAMS_BY_ABBR[abbr];
              const price = state.history.find(h => h.team === abbr && h.winnerId === manager.id)?.price;
              return (
                <div
                  key={abbr}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-800/70 border border-slate-700"
                  title={`${meta?.city} ${meta?.name}${price ? ` · $${price}` : ''}`}
                >
                  <TeamLogo abbr={abbr} size={20} />
                  <span className="text-[10px] font-bold text-slate-300">{abbr}</span>
                  {price != null && <span className="text-[10px] text-emerald-400">${price}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {remaining > 0 && !isComplete && (
        <div className="text-[10px] text-slate-500 mt-2">
          Reserve ${Math.max(0, remaining - 1)} for {remaining - 1} future {remaining - 1 === 1 ? 'team' : 'teams'}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-800/50 rounded-md p-1.5">
      <div className="text-[9px] uppercase text-slate-500">{label}</div>
      <div className="text-sm font-bold text-white">{value}</div>
    </div>
  );
}
